import {
  GetQuestionnaireQuery,
  IPage,
  IQuestionnaire,
  UpdateQuestionnairePage,
} from '@/types/questionnaire'
import axios, { AxiosRequestConfig } from 'axios'
import aspida from '@aspida/axios'
import api from '../../api/$api'
import {
  ConditionEntity,
  CreateConditionDto,
  CreateQuestionnaireDto,
  CreatePageDto,
  QuestionnaireEntity,
  QuestionnairePageEntity,
  UpdateConditionDto,
  UpdateQuestionnaireDto,
  UpdateQuestionDto,
  QuestionEntity,
  CreateQuestionDto,
  GetQuestionnairesResponse,
} from '../../api/@types'

export const getQuestionnaires = async (
  query: GetQuestionnaireQuery | null,
): Promise<IQuestionnaire[]> => {
  const config: AxiosRequestConfig = {
    params: {},
  }

  if (query) {
    config.params = {
      ...config.params,
      ...query,
    }
  }
  const instance = axios.create(config)

  try {
    const { data } = await instance.get<IQuestionnaire[]>('questionnaire')
    return data
  } catch {
    throw new Error('error.questionnaire.getQuestionnaires')
  }
}

export const getQuestionnaire = async (
  id: number,
): Promise<GetQuestionnairesResponse> => {
  const client = api(aspida())

  try {
    const data = await client.questionnaire._id_number(id).$get()
    return data
  } catch {
    throw new Error('error.questionnaire.getQuestionnaire')
  }
}

export const getTargetCount = async (id: number): Promise<number> => {
  const client = api(aspida())

  try {
    const data = await client.questionnaire.count_target._id(id).$get()
    return data
  } catch {
    throw new Error('error.questionnaire.getTargetCount')
  }
}

export const createQuestionnaire = async (
  body: CreateQuestionnaireDto,
): Promise<QuestionnaireEntity> => {
  const instance = api(aspida())

  const data = await instance.questionnaire.$post({
    body,
  })
  return data
}

export const updateQuestionnaire = async (
  id: number,
  body: UpdateQuestionnaireDto,
): Promise<QuestionnaireEntity> => {
  const instance = api(aspida())

  const data = await instance.questionnaire._id_number(id).$put({ body })
  return data
}

export const deleteQuestionnaire = async (
  id: number,
): Promise<QuestionnaireEntity> => {
  const instance = api(aspida())

  const data = await instance.questionnaire._id_number(id).$delete()
  return data
}

export const copyQuestionnaire = async (
  id: number,
): Promise<QuestionnaireEntity> => {
  const instance = api(aspida())

  const data = await instance.questionnaire._id_number(id).copy.$post()
  return data
}

export const additionalIssueQuestionnaire = async (
  id: number,
): Promise<QuestionnaireEntity> => {
  const instance = api(aspida())

  const data = await instance.questionnaire.additional_issue._id(id).$put()
  return data
}

export const createQuestionnairePage = async (
  body: CreatePageDto,
): Promise<QuestionnairePageEntity> => {
  const instance = api(aspida())

  return instance.questionnaire_page.$post({ body })
}

export const updateQuestionnairePage = async (
  pageId: number,
  body: UpdateQuestionnairePage,
): Promise<IPage> => {
  const instance = axios.create()

  const { data } = await instance.put<IPage>(
    `questionnaire-page/${pageId}`,
    body,
  )
  return data
}

export const deleteQuestionnairePage = async (
  pageId: number,
): Promise<boolean> => {
  const instance = axios.create()

  const { data } = await instance.delete<{ deleted: boolean }>(
    `questionnaire-page/${pageId}`,
  )
  return data.deleted
}

export const createQuestion = async (
  body: CreateQuestionDto,
): Promise<QuestionEntity> => {
  const instance = api(aspida())

  return instance.question.$post({ body })
}

export const updateQuestion = async (
  questionId: number,
  body: UpdateQuestionDto,
): Promise<QuestionEntity> => {
  const instance = api(aspida())

  return instance.question._question_id(questionId).$put({ body: body })
}

export const deleteQuestion = async (questionId: number): Promise<boolean> => {
  const instance = axios.create()

  const { data } = await instance.delete<{ deleted: boolean }>(
    `question/${questionId}`,
  )
  return data.deleted
}

export const getConditions = async (
  questionId: number,
): Promise<ConditionEntity[]> => {
  const instance = api(aspida())

  return instance.condition.$get({ query: { questionId } })
}

export const createCondition = async (
  questionId: number,
  body: CreateConditionDto,
): Promise<ConditionEntity> => {
  const instance = api(aspida())

  return instance.condition.$post({ body })
}

export const updateCondition = async (
  id: number,
  body: UpdateConditionDto,
): Promise<ConditionEntity> => {
  const instance = api(aspida())

  return instance.condition._condition_id(id).$put({ body })
}

export const deleteCondition = async (id: number): Promise<boolean> => {
  const instance = api(aspida())

  const data = await instance.condition._condition_id(id).$delete()
  return data.deleted
}

export const getPreviewUrl = async (id: number): Promise<string> => {
  const instance = api(aspida())
  return instance.questionnaire.preview_url._id(id).$get()
}

export const uploadImage = async (file: File): Promise<string> => {
  const instance = api(aspida())
  return instance.gcs.questionnaire_image.$post({ body: { file } })
}

export const downloadAnswersCsvPath = (id: number): string => {
  const instance = api(aspida())
  return instance.questionnaire._id_number(id).answers_csv.$path()
}
